import { map, property, sortedUniqBy } from "lodash";
import { SagaIterator } from "redux-saga";
import { all, call, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import { ToastContent, getToastContent } from "constants/toasts";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { isInIframe } from "helpers/app";
import { ApiManager } from "services/api";
import { AgentSerializer } from "services/api/agent";
import { getItem, removeItem, saveItem } from "services/local-storage";
import { CRUDAction, IEntityFetchSuccess, RequestAction } from "store/entities/actions";
import { AGENT, AgentActions, AgentActionsNames } from "store/entities/agents/actions";
import { INTEGRATION } from "store/entities/integrations";
import { AppActions } from "store/features/app";
import { SessionActions, getCurrentAgentId, selectIsGhostLogin } from "store/features/session";
import { ToastAutoHideDelay, ToastVariant, ToastsActions } from "store/features/toasts";
import { IActionWithPayload } from "store/helper";
import { Theme } from "themes/helpers";

import { TeamActions } from "../teams";

import {
  IAgent,
  IBatchCreateAgentsPayload,
  IDeleteAgentPayload,
  IFetchSinglePayload,
  IFinishWelcomeTutorialPayload,
  ISyncDeleteAgentPayload,
  IUpdateAgentPayload,
} from "./interfaces";
import {
  selectAgent,
  selectAgentsAlphabetically,
  selectLoggedInAgent,
  selectLoggedInAgentWebpushSubscriptions,
} from "./selectors";

export function* fetchSingle(action: IActionWithPayload<string, IFetchSinglePayload>): SagaIterator {
  const { id } = action.payload;

  const response = yield call(ApiManager.agentApi.fetch, id);

  if (response.result) {
    yield put(AgentActions.fetchSuccess({ id, value: AgentSerializer.deserialize(response.result) }));
  } else {
    yield put(AgentActions.fetchFailure({ id, error: null }));
  }
}

export function* fetchCollection(): SagaIterator {
  const response = yield call(ApiManager.agentApi.fetchAll);

  if (response.result) {
    yield put(AgentActions.fetchCollectionSuccess({ values: AgentSerializer.deserializeList(response.result) }));
  } else {
    yield put(AgentActions.fetchCollectionFailure({ error: null }));
  }
}

function* update(action: IActionWithPayload<string, IUpdateAgentPayload>): SagaIterator {
  const valuesToUpdate = { ...action.payload.value };
  const agentId = valuesToUpdate.id;

  if (!agentId) return;

  const currentValues: IAgent = yield select(selectAgent, agentId);

  if (valuesToUpdate.permission && valuesToUpdate.permission === currentValues.permission) {
    // do not override the same permission because it will revoke the access token
    delete valuesToUpdate.permission;
  }

  if (valuesToUpdate.settings) {
    const { weeklyReports: oldWeeklyReports, emailNotificationsActive: oldEmailNotificationsActive } =
      currentValues.settings;

    const { weeklyReports: newWeeklyReports, emailNotificationsActive: newEmailNotificationsActive } =
      valuesToUpdate.settings;

    if (oldEmailNotificationsActive && !newEmailNotificationsActive) {
      trackEngagement("Agent details: Uncheck email notifications", EventPlace.AccountDetails);
    } else if (!oldEmailNotificationsActive && newEmailNotificationsActive) {
      trackEngagement("Agent details: Check email notifications", EventPlace.AccountDetails);
    }

    if (oldWeeklyReports && !newWeeklyReports) {
      trackEngagement("Agent details: Uncheck weekly summary emails", EventPlace.AccountDetails);
    } else if (!oldWeeklyReports && newWeeklyReports) {
      trackEngagement("Agent details: Check weekly summary emails", EventPlace.AccountDetails);
    }
  }

  const response = yield call(ApiManager.agentApi.updateAgent, agentId, valuesToUpdate);

  if (response.result) {
    yield put(AgentActions.updateSuccess({ id: agentId, value: AgentSerializer.deserialize(response.result) }));
  } else {
    yield put(AgentActions.updateFailure({ id: agentId, error: response.error.error.message }));

    const lackingSeats = response.error?.error?.details?.lackingSeats;
    const paymentIssue = response.error?.error?.type === "paymentIssue";

    yield put(
      ToastsActions.createToast({
        variant: ToastVariant.Error,
        content: getToastContent(ToastContent.AGENT_UPDATE_FAILURE, {
          lackingSeats,
          paymentIssue,
        }),
        autoHideDelayTime: lackingSeats || paymentIssue ? ToastAutoHideDelay.Infinite : ToastAutoHideDelay.Long,
      })
    );
  }
}

function* finishWelcomeTutorial(action: IActionWithPayload<string, IFinishWelcomeTutorialPayload>): SagaIterator {
  const { id, flags } = { ...action.payload };
  // @ts-expect-error TS(2769
  yield call(ApiManager.agentApi.updateAgent, id, { flags });
}

function* sendPushTest(action: IActionWithPayload<string, { agentId: string; deviceId: string }>): SagaIterator {
  const { agentId, deviceId } = { ...action.payload };
  yield call(ApiManager.agentApi.sendPushTest, agentId, deviceId);
}

function* changeTheme(action: IActionWithPayload<string, { theme: Theme }>): SagaIterator {
  const { theme } = action.payload;
  const loggedInAgentId = (yield select(getCurrentAgentId)) as string;

  const response = yield call(ApiManager.agentApi.updateAgent, loggedInAgentId, { settings: { theme } });

  if (response.result) {
    yield put(AgentActions.changeThemeSuccess({ agent: AgentSerializer.deserialize(response.result) }));
    if (!isInIframe()) {
      yield put(AppActions.setTheme(theme));
    }
  } else {
    yield put(AgentActions.changeThemeFailure());
  }
}

function* changeAutoAssignment(action: IActionWithPayload<string, { autoassignment: boolean }>): SagaIterator {
  const { autoassignment } = action.payload;
  const loggedInAgentId = yield select(getCurrentAgentId);

  if (!loggedInAgentId) return;

  const response = yield call(ApiManager.agentApi.updateAgent, loggedInAgentId, { autoassignment });

  if (response.result) {
    yield put(AgentActions.changeAutoAssignmentSuccess({ autoassignment, agentId: loggedInAgentId }));
    yield put(
      ToastsActions.createToast({
        content: getToastContent(ToastContent.AUTO_ASSIGNMENT_CHANGED_SUCCESS),
        variant: ToastVariant.Success,
      })
    );
  } else {
    yield put(AgentActions.changeAutoAssignmentFailure());
    ToastsActions.createToast({
      content: getToastContent(ToastContent.AUTO_ASSIGNMENT_CHANGED_FAILURE),
      variant: ToastVariant.Error,
    });
  }
}

function* syncTheme(action: IActionWithPayload<string, IEntityFetchSuccess<IAgent>>): SagaIterator {
  const { id } = action.payload;

  const currentAgent = yield select(selectLoggedInAgent);
  const inFrame = isInIframe();

  if (currentAgent.id === id && !inFrame) {
    yield put(AppActions.setTheme(currentAgent.settings.theme));
  }
}

function* deleteAgent(action: IActionWithPayload<string, IDeleteAgentPayload>): SagaIterator {
  const { id } = action.payload;
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.agentApi.deleteAgent, id);
  const isOwnerDeletion = response.responseStatus === 403;

  if (response.result) {
    yield put(AgentActions.deleteSuccess({ id }));
    // @ts-expect-error TS(2322)
    yield put(TeamActions.syncDeletedAgent({ agentId: id }));
  } else {
    yield put(AgentActions.deleteFailure({ id, isOwnerDeletion }));
  }
}

function* syncDeletedAgent(action: IActionWithPayload<string, ISyncDeleteAgentPayload>): SagaIterator {
  const { id } = action.payload;
  // @ts-expect-error TS(2322)
  yield put(TeamActions.syncDeletedAgent({ agentId: id }));
}

function* batchCreateAgents(action: IActionWithPayload<string, IBatchCreateAgentsPayload>): SagaIterator {
  const payload = { ...action.payload };
  const { requestSource } = payload;

  const deduplicatedAgents = sortedUniqBy(payload.agents, agent => agent.email);
  if (deduplicatedAgents.length === 0) {
    yield put(
      AgentActions.batchCreateAgentsSuccess({
        agents: AgentSerializer.deserializeList([]),
        requestSource,
      })
    );
    return;
  }

  const response = yield call(ApiManager.agentApi.createMany, {
    agents: deduplicatedAgents,
    requestSource: payload.requestSource,
  });
  const error = "Could not create agents.";

  if (response.result) {
    const successItems = map(response.result, property("doc")).filter(Boolean);
    if (successItems.length > 0) {
      yield put(
        AgentActions.batchCreateAgentsSuccess({
          // @ts-expect-error TS(2345)
          agents: AgentSerializer.deserializeList(successItems),
          requestSource,
        })
      );

      const agents = yield select(selectAgentsAlphabetically);
      yield put(TeamActions.syncCreatedAgents({ agents }));
    } else {
      const errorObject = response.result[0].error;
      yield put(AgentActions.batchCreateAgentsFailure({ error, errorObject, requestSource }));
    }
  } else {
    yield put(
      AgentActions.batchCreateAgentsFailure({
        error,
        errorObject: response.error,
        requestSource,
      })
    );
  }
}

function* syncAgentVerification(action: IActionWithPayload<string, IEntityFetchSuccess<IAgent>>): SagaIterator {
  const { id } = action.payload;

  const currentAgent = yield select(selectLoggedInAgent);
  const isGhostLogin = yield select(selectIsGhostLogin);

  if (currentAgent.id === id) {
    yield put(
      SessionActions.saveCurrentAgent({
        id,
        isGhostLogin,
      })
    );
  }
}

function* subscribeAgentDevice(
  action: IActionWithPayload<string, { subscription: Record<string, unknown> }>
): SagaIterator {
  const { subscription } = action.payload;

  const currentAgent = yield select(selectLoggedInAgent);
  const webpushSubs = yield select(selectLoggedInAgentWebpushSubscriptions);
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ids = webpushSubs.map((item: any) => item.ID);

  const payload = {
    type: "webpush",
    data: subscription,
  };

  const response = yield call(ApiManager.agentApi.subscribeAgentDevice, currentAgent.id, payload);

  if (response.result) {
    const duplication = ids.includes(response.result.ID);
    if (!duplication) {
      saveItem(`agent-id:${currentAgent.id}`, response.result.ID);
      yield put(
        AgentActions.subscribeAgentDeviceSuccess({ agentId: currentAgent.id, deviceSubscription: response.result })
      );
      yield put(
        ToastsActions.createToast({
          content: getToastContent(ToastContent.WEBPUSH_SUBSCRIPTION_SUCCESS),
          variant: ToastVariant.Success,
        })
      );
    } else {
      yield put(AgentActions.subscribeAgentDeviceFailure());
      yield put(
        ToastsActions.createToast({
          content: getToastContent(ToastContent.WEBPUSH_SUBSCRIPTION_FAILURE, { type: "DUPLICATION" }),
          variant: ToastVariant.Error,
        })
      );
    }
  } else {
    yield put(AgentActions.subscribeAgentDeviceFailure());
    yield put(
      ToastsActions.createToast({
        content: getToastContent(ToastContent.WEBPUSH_SUBSCRIPTION_FAILURE),
        variant: ToastVariant.Error,
      })
    );
  }
}

function* unsubscribeAgentDevice(action: IActionWithPayload<string, { deviceId: string }>): SagaIterator {
  const { deviceId } = action.payload;
  const currentAgent = yield select(selectLoggedInAgent);
  const itemName = `agent-id:${currentAgent.id}`;

  const response = yield call(ApiManager.agentApi.unsubscribeAgentDevice, currentAgent.id, deviceId);

  if (response.result) {
    if (getItem(itemName) === deviceId) {
      removeItem(`agent-id:${currentAgent.id}`);
    }
    yield put(AgentActions.unsubscribeAgentDeviceSuccess({ agentId: currentAgent.id, deviceId }));
  } else {
    yield put(AgentActions.unsubscribeAgentDeviceFailure());
  }
}

function* setAgentFlag(action: IActionWithPayload<string, { name: string; value: boolean | string }>): SagaIterator {
  const { name, value } = action.payload;
  const currentAgent = yield select(selectLoggedInAgent);

  const response = yield call(ApiManager.agentApi.setAgentFlag, currentAgent.id, name, value);

  if (response.result) {
    yield put(AgentActions.setFlagSuccess({ id: currentAgent.id, name, value }));
  } else {
    yield put(AgentActions.setFlagFailure());
  }
}

function* setLastClosedGoogleVerification(action: IActionWithPayload<string, { id: string }>): SagaIterator {
  const { id } = action.payload;
  yield put(AgentActions.setFlag({ name: "lastClosedGoogleVerification", value: id }));
}

export function* agentsSagas(): SagaIterator {
  yield all([
    takeEvery(AGENT[CRUDAction.FETCH_SINGLE][RequestAction.REQUEST], fetchSingle),
    takeEvery(AGENT[CRUDAction.FETCH_SINGLE][RequestAction.SUCCESS], syncAgentVerification),
    takeEvery(AGENT[CRUDAction.FETCH_SINGLE][RequestAction.SUCCESS], syncTheme),
    takeLatest(
      [
        AGENT[CRUDAction.FETCH_COLLECTION][RequestAction.REQUEST],
        INTEGRATION.LIVECHAT_IMPORT_FINISHED,
        INTEGRATION.LIVECHAT_SELECTIVE_IMPORT_FINISHED,
      ],
      fetchCollection
    ),
    takeEvery(AGENT[CRUDAction.UPDATE][RequestAction.REQUEST], update),
    takeEvery(AGENT[CRUDAction.DELETE][RequestAction.REQUEST], deleteAgent),
    takeEvery(AgentActionsNames.SYNC_DELETE, syncDeletedAgent),
    takeEvery(AGENT.CREATE_BATCH[RequestAction.REQUEST], batchCreateAgents),
    takeEvery(AGENT.SET_FLAG[RequestAction.REQUEST], setAgentFlag),
    takeEvery(AGENT.CHANGE_THEME[RequestAction.REQUEST], changeTheme),
    takeEvery(AGENT.CHANGE_AUTO_ASSIGNMENT[RequestAction.REQUEST], changeAutoAssignment),
    takeLatest(AGENT.FINISH_WELCOME_TUTORIAL[RequestAction.REQUEST], finishWelcomeTutorial),
    takeLatest(AGENT.SEND_PUSH_TEST[RequestAction.REQUEST], sendPushTest),
    takeEvery(AGENT.SUBSCRIBE_DEVICE[RequestAction.REQUEST], subscribeAgentDevice),
    takeEvery(AGENT.UNSUBSCRIBE_DEVICE[RequestAction.REQUEST], unsubscribeAgentDevice),
    takeEvery(AGENT.SET_LAST_CLOSED_GOOGLE_VERIFICATION[RequestAction.REQUEST], setLastClosedGoogleVerification),
  ]);
}
